import React, { useEffect, useState, useRef } from 'react';
import { CountdownTimer } from '../components/CountdownTimer';
import { ReasonCard } from '../components/ReasonCard';
import { ProgressIndicator } from '../components/ProgressIndicator';
import { AuthorCard } from '../components/AuthorCard';
import { CortisolGraph } from '../components/CortisolGraph';
import { SerotoninGraph } from '../components/SerotoninGraph';
import { StickyAddToCart } from '../components/StickyAddToCart';
import { FacebookReviews } from '../components/FacebookReviews';
import { AsSeenIn } from '../components/AsSeenIn';
import { ComparisonTable } from '../components/ComparisonTable';
import { FAQ } from '../components/FAQ';
import { ReviewsSection } from '../components/ReviewsSection';
import { FinalCTA } from '../components/FinalCTA';
import { ResearchBanner } from '../components/ResearchBanner';
import { StatisticsSection } from '../components/StatisticsSection';
import { ScrollProgressBar } from '../components/ScrollProgressBar';
import { Shield, Star, Eye } from 'lucide-react';
import { SellingOutWidget } from '../components/SellingOutWidget';
import { AbsorptionComparisonGraph } from '../components/AbsorptionComparisonGraph';
import { SleepTrackingVisualization } from '../components/SleepTrackingVisualization';
import { MorningEnergyChart } from '../components/MorningEnergyChart';
import { IngredientBreakdown } from '../components/IngredientBreakdown';
export function LyraSleepListicle() {
  const [stickyVisible, setStickyVisible] = useState(false);
  const [blackFridayCtaVisible, setBlackFridayCtaVisible] = useState(false);
  const thirdItemRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStickyVisible(true);
      }
    }, {
      threshold: 0.5
    });
    const blackFridayObserver = new IntersectionObserver(([entry]) => {
      // Show CTA when user scrolls past item 1
      if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
        setBlackFridayCtaVisible(true);
      } else if (entry.isIntersecting) {
        setBlackFridayCtaVisible(false);
      }
    }, {
      threshold: 0
    });
    if (thirdItemRef.current) {
      observer.observe(thirdItemRef.current);
    }
    if (firstItemRef.current) {
      blackFridayObserver.observe(firstItemRef.current);
    }
    return () => {
      observer.disconnect();
      blackFridayObserver.disconnect();
    };
  }, []);
  const productUrl = 'https://lyrasleep.com/products/sleep-gummies';
  const userImages = ['https://uploadthingy.s3.us-west-1.amazonaws.com/wLoC6r8we6i9Y4sS4Apvgb/503649991_4052120671723110_1403674338188493204_n.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/fYZNVZ8cN9mcCFuqh9gGM5/487200938_10238722161099832_978794977940731916_n.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/mFvh2byWk8LMDVxNWNzVhc/524389540_2543861255970628_4540051037418634527_n.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/f54AyofCfsJdRV3BHNqAJd/516047929_10161633251875415_841003803251031693_n.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dcYCEL9M59kcN9tRkhXMxw/400109615_747283720552704_8622057937002730387_n.jpg'];
  const doctorImages = ['https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&h=100&fit=crop&crop=faces', 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100&h=100&fit=crop&crop=faces', 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=100&h=100&fit=crop&crop=faces', 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=100&h=100&fit=crop&crop=faces'];
  const reasons = [{
    number: 1,
    title: 'LyraSleep Helps You Fall Asleep 3x Faster',
    content: {
      paragraphs: ['If you\'ve tried <strong>melatonin</strong> or <strong>Ambien</strong>, you know the frustration of lying there <span class="bg-yellow-200 px-1">wide awake</span>, mind racing, waiting <strong>30-60 minutes</strong> and still feeling restless.', '<strong>LyraSleep works differently.</strong>', 'Its <span class="bg-yellow-200 px-1">clinical-dose formula</span> targets your <strong>serotonin and cortisol levels</strong> directly — the neurochemical imbalance keeping you awake. Most people start to feel calm within <strong>15-20 minutes</strong>.', 'It\'s the first sleep supplement that feels like it <span class="bg-yellow-200 px-1">actually works in real time</span> — without grogginess or next-day fatigue.']
    },
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vibyQ3kPuHyHpJpmseX7TH/ApctBuy2PmSePkq3sjd3vW_converted.webp',
    imageAlt: 'Fast sleep onset'
  }, {
    number: 2,
    title: 'LyraSleep Helps You Sleep Through The Entire Night',
    content: {
      paragraphs: ['Falling asleep is only half the battle — <strong>staying asleep</strong> is where most products fail.', 'That <span class="bg-yellow-200 px-1">3AM wake-up</span>? <strong>That\'s cortisol.</strong>', 'LyraSleep\'s unique blend of <strong>Magnesium Glycinate</strong>, <strong>Passionflower</strong>, and <strong>Valerian Root</strong> regulates your stress hormones and supports <span class="bg-yellow-200 px-1">deep, stable sleep cycles</span> that last all night.', 'You\'ll wake up less, sleep more consistently, and feel <strong>fully rested</strong> when morning comes. For people who constantly wake up at 2-3 a.m. or toss and turn throughout the night, this formula can be a <span class="bg-yellow-200 px-1">complete game-changer</span>.']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/b3a59f26-0d60-4ef2-bfd0-1dfcc79e7875_min_compressed.webp?v=1763293744&width=800',
    imageAlt: 'Uninterrupted sleep'
  }, {
    number: 3,
    title: 'Feel Energized and Rested Each Morning',
    content: {
      paragraphs: ['<strong>Melatonin</strong> and <strong>Ambien</strong> do knock you out — but they also leave you feeling like a <span class="bg-yellow-200 px-1">zombie the next morning</span>. Heavy, groggy, foggy, and sometimes even a little dazed.', '<strong>LyraSleep gives you the same knockout-level sleep, but in a clean, natural way that lets you wake up <span class="bg-yellow-200 px-1">clear and refreshed</span>.</strong>', 'You still fall asleep fast and stay asleep all night, just <strong>without the hangover</strong>, weird dreams, or heavy crash.']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/istockphoto-2080405881-612x612.jpg?v=1760902466&width=800',
    imageAlt: 'Morning energy'
  }, {
    number: 4,
    title: 'Calms Cortisol and Soothes Your Body Into Sleep',
    content: {
      paragraphs: ["When your mind won't stop racing at night, it's usually because your <strong>cortisol</strong> — your body's main <span class=\"bg-yellow-200 px-1\">stress hormone</span> — is still high.", "That's what keeps you <strong>tense and alert</strong> even when you're exhausted.", '<strong>LyraSleep\'s adaptogenic blend helps lower those cortisol levels so your body can finally switch out of <span class="bg-yellow-200 px-1">"fight or flight" mode</span> and into true rest.</strong>', 'As your stress fades, your muscles loosen, your thoughts quiet down, and your body naturally drifts toward <strong>deep, uninterrupted sleep</strong>.']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/Copy_of_LyraSleep_Images_1__converted.webp?v=1763210279&width=800',
    imageAlt: 'Cortisol regulation'
  }, {
    number: 5,
    title: 'LyraSleep Is Made With Clinical-Dose Sleep Ingredients',
    content: {
      paragraphs: ['LyraSleep isn\'t a simple melatonin gummy — it\'s a <span class="bg-yellow-200 px-1">full-spectrum sleep formula</span> built with <strong>clinically-studied doses</strong> that target every stage of sleep.'],
      bullets: ['<strong>Valerian Root Extract (600mg)</strong> — Clinical dose for <strong>anxiety reduction</strong>', '<strong>L-Theanine (200mg)</strong> — The amount used in <span class="bg-yellow-200 px-1">actual clinical trials</span>', '<strong>Magnesium Glycinate (350mg)</strong> — Not the cheap oxide form that causes digestive issues', '<strong>5-HTP (200mg)</strong> — Supports <strong>serotonin production</strong> for deeper sleep', '<strong>Passionflower Extract (400mg)</strong> — Reduces restlessness and eases anxiety'],
      paragraphs2: ['Each ingredient is <strong>third-party tested</strong> and precisely balanced, so you get results that feel <span class="bg-yellow-200 px-1">natural and consistent</span> night after night.', '<strong>Delicious natural berry flavor</strong> — no chalky aftertaste or artificial sweeteners.']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/Untitled_design_7__converted.webp?v=1761387862&width=800',
    imageAlt: 'Clinical ingredients'
  }, {
    number: 6,
    title: 'Trusted and Recommended By 400+ Healthcare Professionals',
    content: {
      paragraphs: ["LyraSleep isn't just another sleep supplement. It's trusted by <strong>more than 400 doctors and sleep specialists</strong> who recommend it to patients struggling with restless nights.", 'The formula combines <span class="bg-yellow-200 px-1">natural, science-backed ingredients</span> that help calm the nervous system, reduce nighttime stress, and guide the body into <strong>real, restorative rest</strong>.', '<strong>Just 2 gummies before bed deliver gentle relaxation without grogginess or dependency</strong> — helping you fall asleep naturally and wake up feeling <span class="bg-yellow-200 px-1">refreshed, not sluggish</span>.']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/f615eb8a-c6fc-44a7-bc50-c642782df0c1_converted.webp?v=1761504439&width=800',
    imageAlt: 'Doctor recommended'
  }, {
    number: 7,
    title: 'LyraSleep Is Safe, Drug-Free, and Habit-Free',
    content: {
      paragraphs: ['LyraSleep is completely <strong>drug-free</strong> and <strong>non-habit forming</strong>, so you can use it nightly without worrying about <span class="bg-yellow-200 px-1">dependency or withdrawal</span>.', "Unlike prescription pills, it works with your body's <strong>natural sleep rhythms</strong> instead of forcing you to knock out.", '<strong>You stay in control of your sleep routine — not reliant on a pill to function.</strong>', 'For many people, switching to LyraSleep means finally <span class="bg-yellow-200 px-1">breaking free from the cycle</span> of sleep aids that stop working over time.']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/5f843546-7fb6-49d8-93b1-96b0bcc0d8e0_converted.webp?v=1761400123&width=800',
    imageAlt: 'Safe and non-habit forming'
  }, {
    number: 8,
    title: 'Already Used Nightly By 300,000+ Sleepers',
    content: {
      paragraphs: ['Over <strong>300,000 people</strong> have made LyraSleep a nightly part of their routine — and <span class="bg-yellow-200 px-1">hundreds of doctors</span> now recommend it to their patients as a safe, effective alternative to pills.', "Many customers say they were skeptical at first after trying everything else, but now call it the <strong>best sleep product they've ever used</strong>.", '<strong>That\'s because LyraSleep isn\'t another "trend" — it\'s a <span class="bg-yellow-200 px-1">clinically designed formula</span> that delivers real, noticeable sleep improvements within days.</strong>']
    },
    imageUrl: 'https://lyrasleep.com/cdn/shop/files/b2562548-15c9-4d93-a903-b228fb69a187_converted.webp?v=1763222262&width=1600',
    imageAlt: '300,000+ customers'
  }];
  return <div className="min-w-full bg-white" data-id="element-569">
      <ScrollProgressBar data-id="element-570" />
      <ProgressIndicator totalSteps={8} data-id="element-571" />

      {/* Blog Header Banner */}
      <div className="bg-white border-b border-gray-200" data-id="element-572">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-573">
          <div className="flex items-center justify-between" data-id="element-574">
            <div className="flex items-center gap-3" data-id="element-575">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/onnKXQGybZpgGJdYNdSE8N/mainlogo_%281%29.webp" alt="Health Insider" className="h-10 w-auto" data-id="element-576" />
            </div>
            <div className="hidden sm:flex items-center gap-6 text-sm text-gray-600" data-id="element-577">
              <a href="#" className="hover:text-purple-600 transition-colors" data-id="element-578">
                Articles
              </a>
              <a href="#" className="hover:text-purple-600 transition-colors" data-id="element-579">
                Research
              </a>
              <a href="#" className="hover:text-purple-600 transition-colors" data-id="element-580">
                About
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Trending Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2" data-id="element-581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-582">
          <div className="flex items-center justify-center gap-3 text-xs sm:text-sm" data-id="element-583">
            <span data-id="element-584">🇺🇸</span>
            <span className="font-bold uppercase tracking-wide" data-id="element-585">
              TRENDING IN THE US
            </span>
            <span className="opacity-90" data-id="element-586">•</span>
            <span className="opacity-90" data-id="element-587">👀 7M Views on Facebook</span>
          </div>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-8" data-id="element-588">
        {/* Header */}
        <header className="text-center mb-12" data-id="element-589">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight" data-id="element-590">
            8 Reasons Why LyraSleep Is{' '}
            <span className="underline decoration-4 decoration-purple-600" data-id="element-591">
              Replacing Melatonin and Sleep Pills
            </span>{' '}
            Across America
          </h1>

          <div className="flex justify-center mb-6" data-id="element-592">
            <AuthorCard name="Dr. Jennifer Hayes, PhD" title="Sleep Researcher, Mayo Clinic" imageUrl="https://cdn.shopify.com/s/files/1/0988/8867/9773/files/3f159058-265b-4a17-ac93-d3f903bf447c_min.webp?v=1760543804" lastUpdated="Last Updated November 15, 2025" data-id="element-593" />
          </div>

          <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-5 py-2.5" data-id="element-594">
            <span className="text-xl" data-id="element-595">👁️</span>
            <span className="text-sm font-semibold text-gray-700" data-id="element-596">
              5,379,325 readers
            </span>
          </div>
        </header>

        {/* New Emotional Opening Section */}
        <div className="max-w-3xl mx-auto mb-12 space-y-8 animate-fade-in" data-id="element-597">
          <h2 className="text-3xl font-bold text-gray-900 text-center sm:text-left" data-id="element-598">
            Sick of Doctors Telling You to{' '}
            <span className="bg-yellow-200 px-2" data-id="element-599">
              "Just Practice Better Sleep Hygiene"
            </span>
            ?
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed" data-id="element-600">
            <p className="text-center sm:text-left" data-id="element-601">
              You've done the <strong data-id="element-602">blackout curtains</strong>. The{' '}
              <strong data-id="element-603">no screens after 9PM</strong>. The{' '}
              <strong data-id="element-604">chamomile tea ritual</strong>.
            </p>

            <p className="text-center sm:text-left" data-id="element-605">
              And you're <em data-id="element-606">STILL</em> lying there at <strong data-id="element-607">3AM</strong>,
              <br className="hidden sm:block" data-id="element-608" />
              <strong data-id="element-609">mind racing</strong>, body exhausted, while your partner
              snores peacefully next to you.
            </p>

            <div className="bg-purple-100 rounded-xl p-8 shadow-md relative animate-slide-up my-8" data-id="element-610">
              <div className="text-6xl text-purple-600 absolute -top-4 left-4" data-id="element-611">
                "
              </div>
              <p className="italic text-xl text-gray-800 pt-4" data-id="element-612">
                Your{' '}
                <span className="bg-yellow-200 px-1" data-id="element-613">cortisol is spiking</span>{' '}
                when it should be lowest, and your{' '}
                <span className="bg-yellow-200 px-1" data-id="element-614">serotonin is tanking</span>{' '}
                when you need it most.
              </p>
            </div>

            <p className="text-center italic text-gray-500 text-base" data-id="element-615">
              No amount of lavender spray fixes a{' '}
              <strong data-id="element-616">neurochemistry problem</strong>.
            </p>

            <p className="bg-yellow-200 px-4 py-2 rounded-lg text-center sm:text-left" data-id="element-617">
              That's because sleep hygiene and other sleep aids don't fix the{' '}
              <strong data-id="element-618">REAL problem</strong>.
            </p>
          </div>

          <p className="text-center text-xl font-bold text-purple-600 my-6" data-id="element-619">
            There's finally a better way.
          </p>

          <p className="text-center text-lg" data-id="element-620">
            <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent font-bold" data-id="element-621">
              LyraSleep Deep Restore Gummies™
            </span>{' '}
            — <strong data-id="element-622">fall asleep faster</strong>,{' '}
            <strong data-id="element-623">sleep through the night</strong>,{' '}
            <strong data-id="element-624">wake up clear</strong>.{' '}
            <span className="bg-yellow-200 px-1" data-id="element-625">
              No melatonin. No prescription. No morning fog.
            </span>
          </p>

          <p className="text-center text-lg font-semibold" data-id="element-626">
            Here's why{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent" data-id="element-627">
              300,000+ chronic insomniacs
            </span>{' '}
            made the switch:
          </p>
        </div>

        {/* Breaking Point Story */}
        <div className="mb-12 rounded-xl overflow-hidden border-l-4 border-purple-600 bg-gray-50" data-id="element-628"></div>

        {/* As Seen In Section */}
        <AsSeenIn data-id="element-629" />

        {/* Enhanced Social Proof Section */}
        <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 rounded-2xl p-4 sm:p-6 md:p-8 my-12 border border-purple-100" data-id="element-630">
          <div className="flex overflow-x-auto md:grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 pb-4 md:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-631">
            <div className="flex-shrink-0 w-64 sm:w-auto text-center" data-id="element-632">
              <div className="flex justify-center mb-3" data-id="element-633">
                <div className="flex -space-x-2" data-id="element-634">
                  {userImages.slice(0, 4).map((img, i) => <img key={i} src={img} alt={`Customer ${i + 1}`} className="w-10 h-10 rounded-full border-2 border-white shadow-lg object-cover" data-id="element-635" />)}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold" data-id="element-636">
                    +300K
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-green-600 mb-1.5" data-id="element-637">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-638">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-639" />
                </svg>
                <span className="font-bold text-base whitespace-nowrap" data-id="element-640">
                  300K+ Customers
                </span>
              </div>
              <p className="text-gray-600 text-xs" data-id="element-641">Happy customers worldwide</p>
            </div>

            <div className="flex-shrink-0 w-64 sm:w-auto text-center" data-id="element-642">
              <div className="flex justify-center mb-3" data-id="element-643">
                <div className="flex -space-x-2" data-id="element-644">
                  {doctorImages.map((img, i) => <img key={i} src={img} alt={`Doctor ${i + 1}`} className="w-10 h-10 rounded-full border-2 border-white shadow-lg object-cover" data-id="element-645" />)}
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-green-600 mb-1.5" data-id="element-646">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-647">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-648" />
                </svg>
                <span className="font-bold text-base whitespace-nowrap" data-id="element-649">
                  Doctor Recommended
                </span>
              </div>
              <p className="text-gray-600 text-xs" data-id="element-650">
                Trusted by healthcare professionals
              </p>
            </div>

            <div className="flex-shrink-0 w-64 sm:w-auto text-center flex flex-col items-center" data-id="element-651">
              <div className="flex justify-center mb-3" data-id="element-652">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg flex items-center justify-center" data-id="element-653">
                  <Shield className="w-6 h-6 text-white" data-id="element-654" />
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-green-600 mb-1.5" data-id="element-655">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-656">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-657" />
                </svg>
                <span className="font-bold text-base whitespace-nowrap text-center" data-id="element-658">
                  Money Back Guarantee
                </span>
              </div>
              <p className="text-gray-600 text-xs" data-id="element-659">
                Risk-free 30 Day Money Back promise
              </p>
            </div>
          </div>
        </div>

        {/* Reasons List */}
        <div className="space-y-12 my-12" data-id="element-660">
          <div ref={firstItemRef} data-id="element-661">
            <ReasonCard {...reasons[0]} data-id="element-662" />
          </div>

          {/* Absorption Graph after item 1 */}
          <AbsorptionComparisonGraph data-id="element-663" />

          <ReasonCard {...reasons[1]} data-id="element-664" />

          {/* Sleep Tracking after item 2 */}
          <SleepTrackingVisualization data-id="element-665" />

          <ReasonCard {...reasons[2]} data-id="element-666" />

          {/* Morning Energy Chart after item 3 */}
          <MorningEnergyChart data-id="element-667" />

          {/* Reviews Section after item 3 */}
          <div className="my-12" data-id="element-668">
            <h3 className="text-2xl font-bold text-center mb-8" data-id="element-669">
              Used and Loved By <strong data-id="element-670">300,000+ Customers</strong>
            </h3>
            <FacebookReviews data-id="element-671" />
          </div>

          <ReasonCard {...reasons[3]} data-id="element-672" />

          {/* Cortisol Graph after item 4 */}
          <CortisolGraph data-id="element-673" />

          <ReasonCard {...reasons[4]} data-id="element-674" />

          {/* Ingredient Breakdown after item 5 */}
          <IngredientBreakdown data-id="element-675" />

          {/* Serotonin Graph after item 5 */}
          <SerotoninGraph data-id="element-676" />

          {reasons.slice(5).map(reason => <ReasonCard key={reason.number} {...reason} data-id="element-677" />)}
        </div>

        {/* Statistics Section */}
        <StatisticsSection data-id="element-678" />

        {/* Enhanced 3D Offer Image */}
        <div className="my-16 flex justify-center" data-id="element-679">
          <div className="relative" data-id="element-680">
            <div className="absolute -top-2 -left-2 w-4 h-4 bg-yellow-400 rounded-full animate-pulse" data-id="element-681"></div>
            <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-pink-400 rounded-full animate-pulse" data-id="element-682"></div>
            <div className="absolute top-1/2 -left-4 w-2 h-2 bg-purple-400 rounded-full animate-pulse" data-id="element-683"></div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl shadow-2xl p-8 md:p-12 text-center text-white mb-12" data-id="element-684">
          <div className="max-w-2xl mx-auto" data-id="element-685">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/20" data-id="element-686">
              <div className="flex flex-col items-center gap-3" data-id="element-687">
                <div className="flex items-center gap-3" data-id="element-688">
                  <span className="text-5xl font-bold" data-id="element-689">4.9</span>
                  <div className="flex gap-1" data-id="element-690">
                    {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-8 h-8 fill-green-500 text-green-500" data-id="element-691" />)}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-1" data-id="element-692">
                  <div className="flex items-center gap-2 text-lg" data-id="element-693">
                    <span className="font-semibold" data-id="element-694">Excellent</span>
                    <span className="opacity-90" data-id="element-695">•</span>
                    <span className="font-semibold" data-id="element-696">2,137</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm opacity-90" data-id="element-697">
                    <span data-id="element-698">reviews on</span>
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bbZBEekJsgPrfF5BvkWfo5/Trustpilot_Logo_%282022%29.svg.webp" alt="Trustpilot" className="h-5" data-id="element-699" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-sm font-semibold mb-4 text-purple-200" data-id="element-700">
              🎁 4 FREE GIFTS WITH YOUR ORDER (WORTH $110+)
            </div>

            {/* Free Gifts Image */}
            <div className="mb-6" data-id="element-701">
              <img src="https://lyrasleep.com/cdn/shop/files/The_Radiansea_9__converted.webp?crop=center&height=380&v=1758726353&width=380" alt="4 Free Gifts" className="w-full max-w-md mx-auto rounded-lg" data-id="element-702" />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-703">
              BUY 2 GET 1 FREE
            </h2>
            <p className="text-lg mb-4 opacity-90" data-id="element-704">LIMITED TIME ONLY</p>

            <div className="mb-6" data-id="element-705">
              <p className="text-white/90 mb-4 text-base" data-id="element-706">
                We are selling out more than expected. Order now before we run
                out again.
              </p>
              <SellingOutWidget data-id="element-707" />
            </div>

            <a href={productUrl} target="_blank" rel="noopener noreferrer" className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform inline-flex items-center gap-2 mb-6 w-full sm:w-auto justify-center" data-id="element-708">
              Check Limited Availability
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-709">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-710" />
              </svg>
            </a>

            <div className="border-t border-white/30 pt-6 mt-6" data-id="element-711">
              <h3 className="text-xl font-bold mb-4" data-id="element-712">
                Try It Risk-Free For 30 Days
              </h3>
              <p className="text-sm opacity-90" data-id="element-713">
                If you don't sleep better or you're not completely happy—full
                refund. No questions asked.
              </p>
              <p className="text-sm opacity-90 mt-2" data-id="element-714">
                You stay in control of your sleep—not dependent on hormones or
                drugs.
              </p>
            </div>
          </div>
        </div>

        {/* Severe Insomnia Objection Section */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 md:p-12 mb-12 border-2 border-gray-200" data-id="element-715">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-716">
            "But My Insomnia Is Really Bad..."
          </h2>

          <div className="max-w-2xl mx-auto space-y-4 text-base text-gray-700 leading-relaxed" data-id="element-717">
            <p data-id="element-718">
              We get it. You're not the "I occasionally have trouble sleeping"
              person. You're the{' '}
              <strong data-id="element-719">
                "I haven't slept more than 4 hours straight in 6 months"
              </strong>{' '}
              person.
            </p>

            <p className="text-lg font-semibold text-gray-900" data-id="element-720">
              Here's the thing: LyraSleep was designed for YOU. Not casual
              sleepers.
            </p>

            <p data-id="element-721">
              The clinical doses are <strong data-id="element-722">3-5x stronger</strong> than typical
              supplements because we're targeting chronic, anxiety-driven
              insomnia.
            </p>

            <div className="bg-white rounded-xl p-6 mt-6 space-y-3" data-id="element-723">
              <div className="flex items-start gap-3" data-id="element-724">
                <span className="text-green-600 font-bold text-xl flex-shrink-0" data-id="element-725">
                  ✓
                </span>
                <span className="text-gray-800" data-id="element-726">
                  If you've been prescribed Ambien, <strong data-id="element-727">you qualify</strong>
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-728">
                <span className="text-green-600 font-bold text-xl flex-shrink-0" data-id="element-729">
                  ✓
                </span>
                <span className="text-gray-800" data-id="element-730">
                  If you've cried at 4AM, <strong data-id="element-731">you qualify</strong>
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-732">
                <span className="text-green-600 font-bold text-xl flex-shrink-0" data-id="element-733">
                  ✓
                </span>
                <span className="text-gray-800" data-id="element-734">
                  If you've Googled "why can't I sleep" at 2AM,{' '}
                  <strong data-id="element-735">you definitely qualify</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center" data-id="element-736">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-737">
            The Only Sleep Gummies That Fix The Reason You Can't Sleep
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 mb-8 text-left" data-id="element-738">
            <div className="flex items-start gap-2" data-id="element-739">
              <span className="text-green-600 font-bold text-lg flex-shrink-0" data-id="element-740">
                ✓
              </span>
              <span className="text-gray-700 text-base" data-id="element-741">
                <strong data-id="element-742">Breaks Your Hyper-Arousal Loop</strong> — The real cause
                of chronic insomnia
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-743">
              <span className="text-green-600 font-bold text-lg flex-shrink-0" data-id="element-744">
                ✓
              </span>
              <span className="text-gray-700 text-base" data-id="element-745">
                <strong data-id="element-746">Clinical Doses</strong> — Not weak "fairy dust" like
                other supplements
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-747">
              <span className="text-green-600 font-bold text-lg flex-shrink-0" data-id="element-748">
                ✓
              </span>
              <span className="text-gray-700 text-base" data-id="element-749">
                <strong data-id="element-750">Sleep 7+ Hours Straight</strong> — No more 3AM wake-ups
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-751">
              <span className="text-green-600 font-bold text-lg flex-shrink-0" data-id="element-752">
                ✓
              </span>
              <span className="text-gray-700 text-base" data-id="element-753">
                <strong data-id="element-754">Wake Up Clear</strong> — No morning fog or grogginess
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-755">
              <span className="text-green-600 font-bold text-lg flex-shrink-0" data-id="element-756">
                ✓
              </span>
              <span className="text-gray-700 text-base" data-id="element-757">
                <strong data-id="element-758">Non-Habit Forming</strong> — Use every night without
                dependency
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-759">
              <span className="text-green-600 font-bold text-lg flex-shrink-0" data-id="element-760">
                ✓
              </span>
              <span className="text-gray-700 text-base" data-id="element-761">
                <strong data-id="element-762">Works In 20 Minutes</strong> — Feel calm, drift off
                naturally
              </span>
            </div>
          </div>

          <a href={productUrl} target="_blank" rel="noopener noreferrer" className="bg-purple-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-purple-700 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform inline-flex items-center gap-2" data-id="element-763">
            Check Limited Availability
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-764">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-765" />
            </svg>
          </a>
        </div>

        <ComparisonTable data-id="element-766" />
        <FAQ data-id="element-767" />
        <ReviewsSection data-id="element-768" />
        <FinalCTA data-id="element-769" />
      </article>

      {/* Black Friday Sticky CTA - Simplified */}
      {blackFridayCtaVisible && <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up" data-id="element-770">
          <div className="bg-white shadow-2xl border-t-2 border-gray-200" data-id="element-771">
            <div className="max-w-7xl mx-auto px-4 py-4 flex justify-center" data-id="element-772">
              <a href={productUrl} target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full text-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105" data-id="element-773">
                Claim Black Friday Offer →
              </a>
            </div>
          </div>
        </div>}

      <StickyAddToCart isVisible={stickyVisible} data-id="element-774" />
    </div>;
}